/**
 * useFirebaseInit Hook
 *
 * Initializes Firebase with modern persistent multi-tab cache.
 * The cache automatically handles synchronization across multiple tabs.
 * Now integrates with Microsoft 365 authentication with automatic token refresh.
 *
 * OPTIMIZATION: Skips re-authentication if user is already authenticated,
 * significantly reducing navigation time between pages.
 */

import { useEffect, useRef } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { initializeFirestore, getFirestore, persistentLocalCache, persistentMultipleTabManager, setLogLevel } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { getAuth, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getMessaging, isSupported } from 'firebase/messaging';
import { useFirebaseStore } from '../store/firebaseStore';
import { firebaseConfig } from '../config';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { logger } from '../utils/logger';

interface UseFirebaseInitProps {
  msalAccount?: AccountInfo | null;
  isAuthenticated: boolean;
  msalInstance?: IPublicClientApplication;
}

/** Safely read an `errorCode` property (MSAL AuthError) off an unknown error. */
function getErrorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'errorCode' in err) {
    const code = (err as { errorCode?: unknown }).errorCode;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** Safely read a `code` property (Firebase FirestoreError) off an unknown error. */
function getFirebaseCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** Safely read an error message off an unknown error. */
function getErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export const useFirebaseInit = ({ msalAccount, isAuthenticated, msalInstance }: UseFirebaseInitProps) => {
  const { setDb, setRtdb, setMessaging, setIsConnected, setIsSecondaryTab, setSessionId, setAuthSource, authSource, isAuthChecking } = useFirebaseStore();
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const backgroundRefreshAttempted = useRef(false);

  useEffect(() => {
    // Wait for the Firebase persistence check to complete before deciding
    if (isAuthChecking) return;

    // If Firebase session was restored but MSAL is not authenticated,
    // attempt a background MSAL silent refresh (non-blocking)
    if (authSource === 'firebase_restored' && !isAuthenticated && !backgroundRefreshAttempted.current) {
      backgroundRefreshAttempted.current = true;

      // Delay to let the restored session settle before attempting MSAL refresh
      const bgRefreshTimeout = setTimeout(async () => {
        try {
          const instance = msalInstance || window.msalInstance;
          if (!instance) return;

          // Get all cached accounts from MSAL
          const accounts = instance.getAllAccounts();
          if (accounts.length === 0) return;

          logger.log('[Firebase] Attempting background MSAL silent refresh...');
          const response = await instance.acquireTokenSilent({
            scopes: ['User.Read'],
            account: accounts[0],
            forceRefresh: false,
          });

          if (response?.idToken) {
            // Request fresh custom token from backend
            const tokenResponse = await fetch('/api/auth/custom-token', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${response.idToken}`,
              },
              body: JSON.stringify({
                email: accounts[0].username,
                displayName: accounts[0].name || accounts[0].username,
              }),
            });

            if (tokenResponse.ok) {
              const { customToken, sessionId } = await tokenResponse.json();
              const app = getApps()[0];
              if (app) {
                const auth = getAuth(app);
                await signInWithCustomToken(auth, customToken);
                if (sessionId) setSessionId(sessionId);
                setAuthSource('msal');
                logger.log('[Firebase] Background MSAL refresh succeeded — upgraded to full auth');
              }
            }
          }
        } catch (error) {
          // Non-fatal — user continues with restored Firebase session
          logger.log('[Firebase] Background MSAL refresh failed (non-fatal):', error);
        }
      }, 2000);

      return () => clearTimeout(bgRefreshTimeout);
    }

    // Don't initialize Firebase until user is authenticated via MSAL
    if (!isAuthenticated || !msalAccount) {
      return;
    }

    /**
     * Acquires a fresh Firebase custom token using MSAL authentication
     * This function can be called both during initialization and for token refresh
     */
    const acquireFirebaseToken = async (): Promise<string | null> => {
      try {
        // Use passed instance or fall back to window (legacy)
        const instance = msalInstance || window.msalInstance;

        if (!instance) {
          throw new Error('MSAL instance not found. Authentication system not initialized properly.');
        }

        // Acquire token silently (from cache if available, or refresh if needed)
        const tokenRequest = {
          scopes: ['User.Read'],
          account: msalAccount,
          forceRefresh: false // Let MSAL decide whether to use cache or refresh
        };

        let accessToken: string;

        const validateToken = (token: string | undefined): token is string => {
          if (!token || typeof token !== 'string') return false;
          return token.split('.').length === 3;
        };

        try {
          const response = await instance.acquireTokenSilent(tokenRequest);
          accessToken = response.idToken;

          if (!validateToken(accessToken)) {
            throw new Error('No valid token received from Microsoft authentication');
          }
        } catch (silentError) {
          logger.warn('[MSAL] Silent token acquisition failed, retrying with forceRefresh:', getErrorCode(silentError) || getErrorMessage(silentError));

          // Retry with forceRefresh before giving up — handles cases where the
          // cached token is stale even after a fresh loginRedirect (e.g., browser
          // privacy settings, conditional access policies, iframe restrictions)
          try {
            const retryResponse = await instance.acquireTokenSilent({ ...tokenRequest, forceRefresh: true });
            accessToken = retryResponse.idToken;

            if (!validateToken(accessToken)) {
              throw new Error('No valid token received from Microsoft authentication (retry)');
            }

            logger.log('[MSAL] Token acquired successfully on forceRefresh retry');
          } catch (retryError) {
            const retryCode = getErrorCode(retryError);
            logger.error('[MSAL] Token acquisition failed after retry:', retryCode || getErrorMessage(retryError));

            // If retry also fails due to interaction required, trigger redirect login
            if (retryCode === 'interaction_required' ||
                retryCode === 'login_required' ||
                retryCode === 'consent_required' ||
                getErrorMessage(retryError).includes('monitor_window_timeout')) {
              await instance.loginRedirect(tokenRequest);
              return null; // Will redirect, so return null
            }

            throw new Error('Microsoft 365 authentication expired. Please refresh the page to sign in again.');
          }
        }

        // Request custom token from backend (with MSAL token for validation)
        const tokenResponse = await fetch('/api/auth/custom-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            email: msalAccount.username,
            displayName: msalAccount.name || msalAccount.username
          })
        });

        if (!tokenResponse.ok) {
          const errorData = await tokenResponse.json();

          // Handle user not registered - redirect to login with rejection message
          if (tokenResponse.status === 403 && errorData.code === 'USER_NOT_REGISTERED') {
            logger.warn('[Firebase] User not registered, clearing local auth state');

            // Clear MSAL's local storage to prevent auto-login loop
            // This avoids the Microsoft sign-out page redirect for better UX
            // MSAL stores data with keys containing 'msal' - clear them all
            const keysToRemove: string[] = [];
            for (let i = 0; i < sessionStorage.length; i++) {
              const key = sessionStorage.key(i);
              if (key && key.toLowerCase().includes('msal')) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach(key => sessionStorage.removeItem(key));

            // Also clear localStorage MSAL keys (MSAL can use either)
            const localKeysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.toLowerCase().includes('msal')) {
                localKeysToRemove.push(key);
              }
            }
            localKeysToRemove.forEach(key => localStorage.removeItem(key));

            // Navigate directly to login page with rejection flag (no Microsoft round-trip)
            window.location.href = '/login?rejected=true';
            return null; // Will redirect
          }

          throw new Error(errorData.error || 'Failed to get custom token');
        }

        const { customToken, sessionId } = await tokenResponse.json();

        // Store session ID for heartbeat authentication (lightweight presence tracking)
        if (sessionId) {
          setSessionId(sessionId);
        }

        return customToken;
      } catch (error) {
        logger.error('[Firebase] Error acquiring token:', error);
        throw error;
      }
    };

    /**
     * Refreshes the Firebase authentication token
     * Called periodically to prevent token expiration
     */
    const refreshFirebaseAuth = async () => {
      try {
        const customToken = await acquireFirebaseToken();
        if (!customToken) {
          // Redirect in progress, no need to continue
          return;
        }

        const app = getApps()[0];
        const auth = getAuth(app);

        // Sign in with the new custom token
        await signInWithCustomToken(auth, customToken);
      } catch (error) {
        logger.error('[Firebase] Token refresh failed:', error);
        // Don't set isConnected to false here - let the user continue
        // working with cached data. They can refresh the page if needed.
      }
    };

    const initFirebase = async () => {
      try {
        // Set Firestore log level to 'error' to reduce console noise
        setLogLevel('error');

        // Check if Firebase app is already initialized
        let app;
        if (getApps().length === 0) {
          app = initializeApp(firebaseConfig);
        } else {
          app = getApps()[0];
        }

        // Initialize or get Firestore with modern cache configuration
        let db;
        try {
          db = initializeFirestore(app, {
            localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
          });
        } catch (error) {
          if (getFirebaseCode(error) === 'failed-precondition' || getErrorMessage(error).includes('already been called')) {
            db = getFirestore(app);
          } else {
            throw error;
          }
        }

        // Initialize Realtime Database (reuse if already set by auth restore)
        const existingState = useFirebaseStore.getState();
        const rtdb = existingState.rtdb || getDatabase(app);
        if (!existingState.rtdb) {
          logger.log('[Firebase] Realtime Database initialized');
        }

        // Initialize Messaging (reuse if already set by auth restore)
        let messaging = existingState.messaging || null;
        if (!existingState.messaging) {
          try {
            if (await isSupported()) {
              messaging = getMessaging(app);
              logger.log('[Firebase] Messaging initialized');
            } else {
              logger.log('[Firebase] Messaging not supported in this browser');
            }
          } catch (err) {
            logger.error('[Firebase] Messaging init error:', err);
          }
        }

        // OPTIMIZATION: Skip re-auth if already authenticated
        const auth = getAuth(app);
        const currentUser = auth.currentUser;

        if (currentUser) {
          setDb(db);
          setRtdb(rtdb);
          setMessaging(messaging);
          setIsConnected(true);
          setAuthSource('msal');

          // Ensure we have a session for heartbeat/presence tracking.
          // On page refresh, Firebase auth is restored from persistence so
          // acquireFirebaseToken() (which creates the backend session) is
          // skipped by this early return. Fire it in the background solely
          // to obtain a sessionId — the returned custom token is discarded.
          const currentSessionId = useFirebaseStore.getState().sessionId;
          if (!currentSessionId) {
            acquireFirebaseToken().catch(() => {
              logger.warn('[Firebase] Background session acquisition failed');
            });
          }

          // Set up token refresh if not already running
          // Uses same 50-minute interval as the normal auth path (before the 1-hour Firebase token expiry)
          if (!refreshIntervalRef.current) {
            refreshIntervalRef.current = setInterval(() => {
              refreshFirebaseAuth();
            }, 50 * 60 * 1000); // 50 minutes
          }

          return;
        }

        // Authenticate with Firebase using custom token from backend
        try {
          const customToken = await acquireFirebaseToken();
          if (!customToken) {
            // Redirect in progress
            return;
          }

          // Sign in with custom token
          await signInWithCustomToken(auth, customToken);

          // Wait for auth state to fully propagate
          await new Promise<void>((resolve) => {
            const unsubscribe = onAuthStateChanged(auth, (user) => {
              if (user) {
                unsubscribe();
                resolve();
              }
            });
          });
        } catch (authError) {
          logger.error('[Firebase] Custom token sign-in failed:', authError);
          throw authError;
        }

        // Store Firebase instances (only after auth is fully ready)
        setDb(db);
        setRtdb(rtdb);
        setMessaging(messaging);
        setIsSecondaryTab(false);
        setIsConnected(true);
        setAuthSource('msal');

        // Set up automatic token refresh every 50 minutes (before the 1-hour Firebase token expiration)
        // This ensures we always have a valid token before the current one expires
        // Real-time permission updates now handled by useUserPermissionsListener (Firestore listener)
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }

        refreshIntervalRef.current = setInterval(() => {
          refreshFirebaseAuth();
        }, 50 * 60 * 1000); // 50 minutes in milliseconds
      } catch (error) {
        logger.error('[Firebase] Initialization error:', error);
        setIsConnected(false);

        // Handle expired token by forcing a re-login
        // We now check for a wider range of errors to catch the "stuck" state
        const errorMessage = getErrorMessage(error);
        if (errorMessage && (
          errorMessage.includes('Token has expired') ||
          errorMessage.includes('Invalid Microsoft 365 authentication') ||
          errorMessage.includes('Microsoft 365 authentication expired') ||
          errorMessage.includes('MSAL instance not found') || // Race condition failure
          errorMessage.includes('monitor_window_timeout') // Timeout
        )) {
          logger.warn('[Firebase] Authentication expired or failed. Clearing local auth state and redirecting to login...');

          // Clear MSAL cache locally to prevent auto-login loop
          // Previously used logoutRedirect() which sent users to Microsoft's
          // logout endpoint ("choose which account to sign out of" screen)
          // instead of back to our login page
          const keysToRemove: string[] = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.toLowerCase().includes('msal')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => sessionStorage.removeItem(key));

          const localKeysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.toLowerCase().includes('msal')) {
              localKeysToRemove.push(key);
            }
          }
          localKeysToRemove.forEach(key => localStorage.removeItem(key));

          // Redirect directly to login page (no Microsoft round-trip)
          window.location.href = '/login?reason=session_expired';
        }
      }
    };

    initFirebase();

    // Cleanup: Clear the refresh interval when component unmounts or deps change
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [setDb, setRtdb, setMessaging, setIsConnected, setIsSecondaryTab, setSessionId, setAuthSource, isAuthenticated, isAuthChecking, authSource, msalAccount, msalInstance]);

  // Note: Location selection is now handled by usePagePermissions hook in components
};
